import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en";
import { toast } from "react-toastify";

TimeAgo.addDefaultLocale(en);
const timeAgoFormatter = new TimeAgo("en-US");

const snakeToCamel = (str: string): string =>
  str
    .toLowerCase()
    .replace(/([-_][a-z])/g, (group) =>
      group.toUpperCase().replace("-", "").replace("_", ""),
    );

function camelToSnake(key: string) {
  let result = key.replace(/([A-Z])/g, " $1");
  return result.split(" ").join("_").toLowerCase();
}

export const snakeToCamelObj = (obj: object): object => {
  if (typeof obj !== "object") {
    return {};
  }

  if (Array.isArray(obj)) {
    let res: any = [];
    for (let i = 0; i < obj.length; i++) {
      let val = obj[i];
      if (typeof val === "object") {
        res.push(snakeToCamelObj(val));
      } else {
        res.push(val);
      }
    }
    return res;
  }

  let res: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === "object" && value !== null) {
      res[snakeToCamel(key)] = snakeToCamelObj(value);
    } else {
      res[snakeToCamel(key)] = value;
    }
  }

  return res;
};

export const camelToSnakeObj = (obj: any): object | null => {
  if (obj == null) {
    return null;
  }

  if (Array.isArray(obj)) {
    let res: any = [];
    for (let i = 0; i < obj.length; i++) {
      let val = obj[i];
      if (typeof val === "object") {
        res.push(camelToSnakeObj(val));
      } else {
        res.push(val);
      }
    }
    return res;
  }

  let res: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === "object") {
      res[camelToSnake(key)] = camelToSnakeObj(value);
    } else {
      res[camelToSnake(key)] = value;
    }
  }

  return res;
};

export const isValidEmail = (email: string): boolean => {
  const validRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
  return !!email.match(validRegex);
};

export const getSession = (token: string): string => {
  const key = `_wizychat_session_${token}`;

  let session = getCookie(key);
  if (!session) {
    session = window.localStorage.getItem(key);
  }

  if (!session) {
    session = uuidv4();
    setCookie(key, session, 180);
    window.localStorage.setItem(key, session);
  }

  return session;
};

export const resetSession = (token: string) => {
  const key = `_wizychat_session_${token}`;
  eraseCookie(key);
  window.localStorage.removeItem(key);
};

function uuidv4() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function setCookie(name: string, value: string, days: number) {
  var expires = "";
  if (days) {
    var date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + (value || "") + expires + "; path=/";
}

function getCookie(name: string) {
  var nameEQ = name + "=";
  var ca = document.cookie.split(";");
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) == " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

function eraseCookie(name: string) {
  document.cookie = name + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
}

export function timeAgo(date: Date): string {
  return timeAgoFormatter.format(date);
}

const S2TLangs = [
  ["Afrikaans", ["af-ZA"]],
  ["Bahasa Indonesia", ["id-ID"]],
  ["Bahasa Melayu", ["ms-MY"]],
  ["Català", ["ca-ES"]],
  ["Čeština", ["cs-CZ"]],
  ["Deutsch", ["de-DE"]],
  [
    "English",
    ["en-AU", "Australia"],
    ["en-CA", "Canada"],
    ["en-IN", "India"],
    ["en-NZ", "New Zealand"],
    ["en-ZA", "South Africa"],
    ["en-GB", "United Kingdom"],
    ["en-US", "United States"],
  ],
  [
    "Español",
    ["es-AR", "Argentina"],
    ["es-BO", "Bolivia"],
    ["es-CL", "Chile"],
    ["es-CO", "Colombia"],
    ["es-CR", "Costa Rica"],
    ["es-EC", "Ecuador"],
    ["es-SV", "El Salvador"],
    ["es-ES", "España"],
    ["es-US", "Estados Unidos"],
    ["es-GT", "Guatemala"],
    ["es-HN", "Honduras"],
    ["es-MX", "México"],
    ["es-NI", "Nicaragua"],
    ["es-PA", "Panamá"],
    ["es-PY", "Paraguay"],
    ["es-PE", "Perú"],
    ["es-PR", "Puerto Rico"],
    ["es-DO", "República Dominicana"],
    ["es-UY", "Uruguay"],
    ["es-VE", "Venezuela"],
  ],
  ["Euskara", ["eu-ES"]],
  ["Français", ["fr-FR"]],
  ["Galego", ["gl-ES"]],
  ["Hrvatski", ["hr_HR"]],
  ["IsiZulu", ["zu-ZA"]],
  ["Íslenska", ["is-IS"]],
  ["Italiano", ["it-IT", "Italia"], ["it-CH", "Svizzera"]],
  ["Magyar", ["hu-HU"]],
  ["Nederlands", ["nl-NL"]],
  ["Norsk bokmål", ["nb-NO"]],
  ["Polski", ["pl-PL"]],
  ["Português", ["pt-BR", "Brasil"], ["pt-PT", "Portugal"]],
  ["Română", ["ro-RO"]],
  ["Slovenčina", ["sk-SK"]],
  ["Suomi", ["fi-FI"]],
  ["Svenska", ["sv-SE"]],
  ["Türkçe", ["tr-TR"]],
  ["български", ["bg-BG"]],
  ["Pусский", ["ru-RU"]],
  ["Српски", ["sr-RS"]],
  ["한국어", ["ko-KR"]],
  [
    "中文",
    ["cmn-Hans-CN", "普通话 (中国大陆)"],
    ["cmn-Hans-HK", "普通话 (香港)"],
    ["cmn-Hant-TW", "中文 (台灣)"],
    ["yue-Hant-HK", "粵語 (香港)"],
  ],
  ["日本語", ["ja-JP"]],
  ["Lingua latīna", ["la"]],
];

export const getS2TLangs = (): any[] => {
  let res = [];
  for (let i = 0; i < S2TLangs.length; i++) {
    for (let j = 1; j < S2TLangs[i].length; j++) {
      res.push({
        value: S2TLangs[i][j][0],
        label:
          S2TLangs[i][0] + (S2TLangs[i][j][1] ? ` - ${S2TLangs[i][j][1]}` : ""),
      });
    }
  }
  return res;
};

export const getS2TLang = (token: string): any => {
  const key = `_wizychat_speech_lang_${token}`;

  let lang = getCookie(key);
  if (!lang) {
    lang = window.localStorage.getItem(key);
  }

  if (!lang) {
    return "en-US";
  }

  return lang;
};

export const S2TSaveLang = (token: string, lang: string) => {
  const key = `_wizychat_speech_lang_${token}`;
  setCookie(key, lang, 180);
  window.localStorage.setItem(key, lang);
};

export function getTimestamp() {
  return Math.floor(Date.now() / 1000);
}

export function alertError(message: string): void {
  toast.error(message, {
    position: "top-right",
    autoClose: 3000,
    theme: "light",
  });
}

export function alertSuccess(message: string): void {
  toast.success(message, {
    position: "top-right",
    autoClose: 3000,
    theme: "light",
  });
}

export function scrollToAnchor() {
  setTimeout(() => {
    const hash = window.location.hash.slice(1); // Remove the '#' character from the hash
    if (hash) {
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({behavior: 'smooth'});
      }
    }
  }, 500)
}

export function localStorageGetObject(key: string) : any {
  let json = localStorage.getItem(key);
  if (json !== null) {
    try {
      return JSON.parse(json);
    } catch (e) {
      console.error(e)
    }
  }

  return null
}

export function localStorageSetObject(key: string, data: any) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    console.error(e)
  }
}

export function isSubset(subset: string[], set: string[]): boolean {
  if (!set) {
    return false;
  }
  if (!subset) {
    return true;
  }
  return subset.every((item) => set.includes(item));
}